/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  CheckCircle,
  Rocket,
  Shield,
  Sparkles,
  Star,
  TrendingUp,
  Building2,
  GraduationCap,
  ShoppingBag,
  Globe,
  Users,
  Zap,
} from 'lucide-react';

const AboutMain = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Rocket,
      iconColor: "text-blue-500",
      gradient: "from-blue-500 to-cyan-500",
      title: 'Fast & Reliable',
      description: 'Lightning-fast performance with 99.9% uptime guarantee for uninterrupted business operations.'
    },
    {
      icon: Shield,
      iconColor: "text-green-500",
      gradient: "from-green-500 to-emerald-500",
      title: 'Secure & Trusted',
      description: 'Enterprise-grade security with end-to-end encryption and compliance with global standards.'
    },
    {
      icon: TrendingUp,
      iconColor: "text-purple-500",
      gradient: "from-purple-500 to-pink-500",
      title: 'Scalable Solutions',
      description: 'Grow your business effortlessly with our scalable infrastructure that adapts to your needs.'
    }
  ];

  const values = [
    {
      title: 'Innovation',
      description: 'We constantly push boundaries to deliver cutting-edge solutions that drive business transformation.',
      icon: Sparkles,
      iconColor: "text-blue-500",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      title: 'Excellence',
      description: 'We strive for perfection in every detail, ensuring the highest quality standards in our products.',
      icon: Award,
      iconColor: "text-amber-500",
      gradient: "from-amber-500 to-orange-500",
    },
    {
      title: 'Customer First',
      description: 'Our customers are at the heart of everything we do, guiding our decisions and innovations.',
      icon: Star,
      iconColor: "text-purple-500",
      gradient: "from-purple-500 to-pink-500",
    }
  ];

  const industries = [
    {
      icon: Building2,
      label: "FlexOffice",
      description: "Coworking & flex space management",
      gradient: "from-blue-500 to-cyan-500",
      href: "/cowork-management-software",
    },
    {
      icon: GraduationCap,
      label: "Academy",
      description: "Education & course delivery",
      gradient: "from-purple-500 to-pink-500",
      href: "/academy-management-software",
    },
    {
      icon: ShoppingBag,
      label: "Retail",
      description: "Store & inventory management",
      gradient: "from-green-500 to-emerald-500",
      href: "/retail-management-software",
    },
  ];

  const checklist = [
    'All-in-one business management platform',
    'Intuitive and user-friendly interface',
    'Scalable for businesses of all sizes',
    '24/7 dedicated customer support',
    'Regular updates and new features',
    'Secure and compliant infrastructure',
  ];

  return (
    <div className="bg-white min-h-screen">

      {/* ── HERO ── */}
      <div className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50/40 border-b border-slate-200 py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-200/30 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: `radial-gradient(circle at 1.5px 1.5px, #334155 1px, transparent 0)`, backgroundSize: '32px 32px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-2xl text-base font-bold shadow-2xl">
                <Globe className="w-5 h-5" />
                <span>About Krumago</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight">
                Empowering Businesses Through{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Innovation
                </span>
              </h1>

              <p className="text-xl text-slate-600 leading-relaxed font-light">
                Krumago is a comprehensive business solution platform designed to streamline
                operations, enhance productivity, and drive growth for companies of all sizes.
              </p>

              {/* Industry Pills */}
              <div className="flex flex-wrap gap-3 pt-2">
                {industries.map((ind) => {
                  const Icon = ind.icon;
                  return (
                    <button
                      key={ind.label}
                      onClick={() => navigate(ind.href)}
                      className={`group inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${ind.gradient} text-white text-sm font-bold rounded-2xl hover:scale-105 transition-all duration-300 shadow-lg`}
                    >
                      <Icon className="w-4 h-4" />
                      {ind.label}
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right — checklist card */}
            <div className="relative">
              <div className="bg-white border border-slate-200/80 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, #0f172a 1px, transparent 0)`, backgroundSize: '20px 20px' }} />
                <div className="relative">
                  <h3 className="text-2xl font-black text-slate-900 mb-8 flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    Why Choose{' '}
                    <span className="font-serif font-black">
                      <span className="text-[rgb(0,0,168)]">Kru</span>
                      <span className="text-[rgb(227,6,19)]">Ma</span>
                      <span className="text-[rgb(0,0,168)]">go</span>
                    </span>
                  </h3>

                  <div className="space-y-4">
                    {checklist.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 group cursor-pointer">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors duration-300">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                        <span className="text-slate-600 group-hover:text-slate-900 transition-colors duration-300 text-lg font-light">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating decorations */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-400/15 rounded-full blur-xl animate-pulse" />
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-purple-400/15 rounded-full blur-xl animate-pulse delay-700" />
            </div>
          </div>
        </div>
      </div>

      {/* ── FEATURES ── */}
      <div className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl opacity-40 animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl opacity-40 animate-pulse delay-1000" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl text-lg font-bold mb-8 shadow-2xl">
              <Zap className="w-6 h-6 text-white" />
              <span>OUR CORE FEATURES</span>
            </div>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-slate-900 mb-8 tracking-tight">
              Everything You Need to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Succeed
              </span>
            </h2>
            <p className="text-xl lg:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-light">
              Designed to meet the diverse needs of modern businesses with powerful,
              intuitive tools that drive success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="group relative">
                  <div className="relative h-full bg-white rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-4 border border-slate-200/60 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                    <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`, backgroundSize: '20px 20px' }} />

                    <div className="relative mb-8">
                      <div className={`w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br ${feature.gradient} p-[2px] transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                        <div className="w-full h-full bg-white rounded-[22px] flex items-center justify-center">
                          <Icon className={`w-9 h-9 ${feature.iconColor}`} strokeWidth={1.75} />
                        </div>
                      </div>
                    </div>

                    <div className="relative space-y-4 text-center">
                      <h3 className={`text-2xl font-bold text-slate-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:${feature.gradient} transition-all duration-500 leading-tight`}>
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed text-lg font-light">
                        {feature.description}
                      </p>
                    </div>

                    <div className={`absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-3xl`} />
                  </div>
                  <div className={`absolute -inset-4 bg-gradient-to-br ${feature.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10`} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── VALUES ── */}
      <div className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100/30 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-purple-600 text-white px-8 py-4 rounded-2xl text-lg font-bold mb-8 shadow-2xl">
              <Award className="w-6 h-6 text-white" />
              <span>OUR VALUES</span>
            </div>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-slate-900 mb-8 tracking-tight">
              What Drives Us{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-purple-600">
                Forward
              </span>
            </h2>
            <p className="text-xl lg:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-light">
              Our core principles guide every decision we make and every product we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="group relative">
                  <div className="relative h-full bg-white rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-4 border border-slate-200/60 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

                    <div className="relative mb-8">
                      <div className={`w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br ${value.gradient} p-[2px] transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                        <div className="w-full h-full bg-white rounded-[22px] flex items-center justify-center">
                          <Icon className={`w-9 h-9 ${value.iconColor}`} strokeWidth={1.75} />
                        </div>
                      </div>
                    </div>

                    <div className="relative space-y-4 text-center">
                      <h3 className="text-2xl font-bold text-slate-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-amber-500 group-hover:to-purple-600 transition-all duration-500">
                        {value.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed text-lg font-light">
                        {value.description}
                      </p>
                    </div>

                    <div className={`absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r ${value.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-3xl`} />
                  </div>
                  <div className={`absolute -inset-4 bg-gradient-to-br ${value.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10`} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── INDUSTRIES STRIP ── */}
      <div className="relative py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl text-lg font-bold mb-6 shadow-2xl">
              <Users className="w-6 h-6 text-white" />
              <span>EXPLORE OUR PRODUCTS</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
              Built for Multiple Industries
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {industries.map((ind) => {
              const Icon = ind.icon;
              return (
                <button
                  key={ind.label}
                  onClick={() => navigate(ind.href)}
                  className="group relative bg-white border border-slate-200/60 rounded-3xl p-8 text-left hover:border-transparent transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${ind.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${ind.gradient} text-white rounded-2xl mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h4 className="font-black text-slate-900 text-2xl mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-500">
                    {ind.label}
                  </h4>
                  <p className="text-slate-500 text-base font-light mb-4">{ind.description}</p>
                  <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm">
                    <span>Explore</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                  <div className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${ind.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-3xl`} />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-100">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-12 lg:p-16 text-white shadow-3xl relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: '40px 40px' }} />
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />

            <div className="relative text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-5 py-2.5 rounded-full border border-white/20 mb-8">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">Get Started Today</span>
              </div>

              <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
                Ready to Transform Your Business?
              </h3>

              <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
                Join hundreds of businesses that trust Krumago to power their operations
                and drive growth.
              </p>

              <button
                onClick={() => navigate('/contact')}
                className="group inline-flex items-center gap-4 px-12 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-black rounded-2xl hover:from-blue-700 hover:to-purple-700 transition-all duration-500 transform hover:scale-105 shadow-2xl"
              >
                Contact Us
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AboutMain;